import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Header.css";
import Logo from "../../assets/images/logo (2).png";
import DropdownIcon from "../../assets/svg/drop_down.svg";
import DropdownActiveIcon from "../../assets/svg/drop_down_active.svg";

const DROPDOWN_ITEMS = {
  "/learn": [{ label: "Blogs", path: "/learn/blogs" }],
  "/support": [
    { label: "FAQs", path: "/support/faqs" },
    { label: "Contact Us", path: "/support/contact" },
  ],
};

const Header = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Track which dropdown is open via click
  const [clickedDropdown, setClickedDropdown] = useState(null);
  // Track which dropdown is being hovered
  const [hoveredDropdown, setHoveredDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const isExactHomePage = location.pathname === "/home";

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close clicked dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (clickedDropdown) {
        const ref = dropdownRefs.current[clickedDropdown];
        if (ref && !ref.contains(e.target)) {
          setClickedDropdown(null);
        }
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [clickedDropdown]);

  const navLinks = [
    { label: "Home", path: "/home" },
    { label: "About", path: "/about" },
    { label: "Build Green", path: "/build-green", green: true },
    { label: "Learn", path: "/learn", dropdown: true },
    { label: "Support", path: "/support", dropdown: true },
  ];

  const isActive = (path) => {
    if (path === "/home") {
      return location.pathname === "/" || location.pathname.startsWith("/home");
    }

    return location.pathname.startsWith(path);
  };

  // A dropdown is open only when clicked
  const isDropdownOpen = (path) => clickedDropdown === path;

  const handleDropdownClick = (path, e) => {
    e.preventDefault();
    setClickedDropdown((prev) => (prev === path ? null : path));
  };

  return (
    <header
      className={`est-header ${
        scrolled ? "est-header--scrolled" : ""
      } ${!isExactHomePage ? "est-header--inner-page" : ""}`}
    >
      <div className="est-header__inner">
        <div className="est-header-right">
          {/* Logo */}
          <Link to="/" className="est-header__logo">
            <div className="est-logo-icon">
              <img src={Logo} alt="Estetigate Logo" />
            </div>

            <span className="est-header__logo-text">ESTETIGATE™</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="est-nav">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              const open = link.dropdown && isDropdownOpen(link.path);

              return link.dropdown ? (
                <div
                  key={link.label}
                  className="est-nav__dropdown-wrapper"
                  ref={(el) => (dropdownRefs.current[link.path] = el)}
                >
                  <button
                    className={`est-nav__link est-nav__link--btn ${
                      link.green ? "est-nav__link--green" : ""
                    } ${active ? "est-nav__link--active" : ""}`}
                    onClick={(e) => handleDropdownClick(link.path, e)}
                    aria-expanded={open}
                  >
                    {link.label}
                    <img
                      src={active || open ? DropdownActiveIcon : DropdownIcon}
                      alt="Dropdown"
                      className={`est-dropdown-icon ${open ? "est-dropdown-icon--open" : ""}`}
                    />
                  </button>

                  {/* Dropdown Menu */}
                  <div
                    className={`est-dropdown ${open ? "est-dropdown--open" : ""}`}
                  >
                    <div className="est-dropdown__inner">
                      {DROPDOWN_ITEMS[link.path]?.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          className={`est-dropdown__item ${
                            location.pathname === item.path
                              ? "est-dropdown__item--active"
                              : ""
                          }`}
                          onClick={() => {
                            setClickedDropdown(null);
                          }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`est-nav__link ${
                    link.green ? "est-nav__link--green" : ""
                  } ${active ? "est-nav__link--active" : ""}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Auth Buttons */}
        <div className="est-header__actions">
          {/* <Link to="/auth" className="est-btn-login">
            Login
          </Link> */}
          <a
            href="https://play.google.com/store/apps/details?id=com.estetipropertyapp&hl=en"
            target="_blank"
            rel="noopener noreferrer"
            className="est-btn-started"
          >
            Get Started
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="est-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span className={menuOpen ? "bar open" : "bar"}></span>
          <span className={menuOpen ? "bar open" : "bar"}></span>
          <span className={menuOpen ? "bar open" : "bar"}></span>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="est-mobile-menu">
          {navLinks.map((link) => (
            <div key={link.label}>
              {link.dropdown ? (
                <>
                  <button
                    type="button"
                    className={`est-mobile-menu__link ${
                      link.green ? "green" : ""
                    } ${
                      mobileDropdown === link.path || isActive(link.path)
                        ? "est-mobile-menu__link--open"
                        : ""
                    }`}
                    onClick={() =>
                      setMobileDropdown(
                        mobileDropdown === link.path ? null : link.path,
                      )
                    }
                  >
                    {link.label}
                    <img
                      src={
                        mobileDropdown === link.path || isActive(link.path)
                          ? DropdownActiveIcon
                          : DropdownIcon
                      }
                      alt="Dropdown"
                    />
                  </button>

                  {(mobileDropdown === link.path || isActive(link.path)) && (
                    <div className="est-mobile-submenu">
                      {DROPDOWN_ITEMS[link.path]?.map((item) => (
                        <Link
                          key={item.label}
                          to={item.path}
                          className={`est-mobile-submenu__item ${
                            location.pathname === item.path
                              ? "est-mobile-submenu__item--active"
                              : ""
                          }`}
                          onClick={() => {
                            setMenuOpen(false);
                            setMobileDropdown(null);
                          }}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.path}
                  className={`est-mobile-menu__link ${
                    link.green ? "green" : ""
                  } ${
                    isActive(link.path) ? "est-mobile-menu__link--active" : ""
                  }`}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )}
            </div>
          ))}
          <div className="est-mobile-menu__actions">
            {/* <Link
              to="/auth"
              className="est-btn-login"
              onClick={() => setMenuOpen(false)}
            >
              Login
            </Link> */}
            <a
              href="https://play.google.com/store/apps/details?id=com.estetipropertyapp"
              target="_blank"
              rel="noopener noreferrer"
              className="est-btn-started"
              onClick={() => setMenuOpen(false)}
            >
              Get Started
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
